<app-timeout></app-timeout>

<div class="container-fluid app-content-view">
<div class="dq-gate-review-container">

    <div class="page-header">
        <h2>Data Quality Gate — Review Queue</h2>
        <p class="page-subtitle">
            Pending violations:
            <strong>{{ violationBatchCount }}</strong>
            <span class="text-muted">(quarantine batches awaiting review)</span>
        </p>
    </div>

    <div class="tab-toolbar">
        <button class="btn btn-sm btn-outline-secondary"
                (click)="loadPending()"
                [disabled]="isLoadingPending">
            <span *ngIf="isLoadingPending" class="spinner-border spinner-border-sm"></span>
            Refresh
        </button>
    </div>

    <div *ngIf="isLoadingPending" class="loading-indicator">
        Loading quarantine list…
    </div>

    <div *ngIf="!isLoadingPending && pendingRecords.length === 0" class="no-records">
        No pending quarantines.
    </div>

    <div *ngIf="!isLoadingPending && pendingRecords.length > 0" class="quarantine-accordion">

        <div *ngFor="let r of pendingRecords"
             class="quarantine-panel"
             [class.expanded]="isExpanded(r.QuarantineId)">

            <!-- ── Batch header ─────────────────────────────────────────── -->
            <button type="button"
                    class="quarantine-header"
                    (click)="toggleQuarantine(r)">
                <span class="chevron" aria-hidden="true">
                    {{ isExpanded(r.QuarantineId) ? '▾' : '▸' }}
                </span>
                <span class="header-main">
                    <span class="mf-code">{{ r.MemberFirmCode || '—' }}</span>
                    <span class="status-badge status-{{ r.Status | lowercase }}">{{ r.Status }}</span>
                    <span class="meta">
                        Violations: <strong>{{ r.ViolationCount }}</strong>
                        &nbsp;|&nbsp;
                        Snapshot records: <strong>{{ r.SnapshotCount }}</strong>
                        &nbsp;|&nbsp;
                        {{ r.QuarantinedAt | date:'short' }}
                    </span>
                </span>
                <span class="header-ids">
                    <span class="header-id" [title]="'Quarantine: ' + r.QuarantineId">QID: {{ r.QuarantineId | slice:0:8 }}…</span>
                    <span class="header-id" [title]="'Run ID: ' + r.RunId">RUN: {{ r.RunId | slice:0:8 }}…</span>
                </span>
            </button>

            <!-- ── Expanded body: Details + Snapshots ───────────────────── -->
            <div *ngIf="isExpanded(r.QuarantineId)" class="quarantine-body">

                <div *ngIf="panelState[r.QuarantineId]?.loading" class="loading-indicator">
                    Loading details and snapshot records…
                </div>

                <ng-container *ngIf="!panelState[r.QuarantineId]?.loading">

                    <div class="panel-actions" *ngIf="readWriteAccess">
                        <ng-container *ngIf="r.Status === 'Pending' || r.Status === 'PartiallyReviewed'">
                            <button class="btn btn-sm btn-success"
                                    [disabled]="!canAct(r.QuarantineId)"
                                    (click)="promptApprove(r.QuarantineId)"
                                    title="Approve selected records — marked for later apply; removed from pending review">
                                Approve
                            </button>
                            <button class="btn btn-sm btn-danger"
                                    [disabled]="!canAct(r.QuarantineId)"
                                    (click)="promptReject(r.QuarantineId)"
                                    title="Reject / discard selected snapshot records">
                                Reject
                            </button>
                            <span class="selection-hint" *ngIf="getSelectedCount(r.QuarantineId) === 0">
                                Select one or more snapshot records to enable Approve / Reject.
                            </span>
                            <span class="selection-hint" *ngIf="getSelectedCount(r.QuarantineId) > 0">
                                {{ getSelectedCount(r.QuarantineId) }} selected
                            </span>
                        </ng-container>
                        <ng-container *ngIf="r.Status === 'RetryApproved'">
                            <span class="selection-hint">
                                Snapshot records have been approved and are queued for the next sync run retry.
                            </span>
                        </ng-container>
                        <ng-container *ngIf="r.Status === 'Failed' && r.RetryCount < 3">
                            <button class="btn btn-sm btn-warning"
                                    [disabled]="!canRetry(r.QuarantineId)"
                                    (click)="promptRetry(r.QuarantineId)"
                                    title="Reset this failed quarantine so the apply process retries it (max 3 attempts)">
                                Retry ({{ r.RetryCount }}/3)
                            </button>
                            <span class="selection-hint">
                                The apply process failed with an exception. Click Retry to requeue it (attempt {{ r.RetryCount + 1 }} of 3).
                            </span>
                        </ng-container>
                        <ng-container *ngIf="r.Status === 'Failed' && r.RetryCount >= 3">
                            <span class="selection-hint text-danger">
                                Maximum retry attempts (3/3) reached. Manual intervention required.
                            </span>
                        </ng-container>
                    </div>

                    <!-- Violations (aggregate threshold rows) -->
                    <section class="section-block">
                        <h3>Violations</h3>
                        <ng-container *ngIf="getThresholdViolations(panelState[r.QuarantineId]?.details) as thresholdRows">
                            <div *ngIf="thresholdRows.length === 0" class="no-records">
                                No violation detail records.
                            </div>
                            <div *ngIf="thresholdRows.length > 0" class="violations-table-wrap">
                                <table class="table table-sm dq-gate-table violations-table">
                                    <thead>
                                        <tr>
                                            <th class="col-rule">Rule</th>
                                            <th class="col-severity">Severity</th>
                                            <th class="col-mf">Member Firm</th>
                                            <th class="col-description">Description</th>
                                            <th class="col-actual">Actual</th>
                                            <th class="col-threshold">Threshold</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr *ngFor="let d of thresholdRows"
                                            [class.severity-hard]="d.Severity === 'Hard'"
                                            [class.severity-soft]="d.Severity === 'Soft'">
                                            <td class="col-rule cell-clip" [title]="d.RuleKey">{{ d.RuleKey }}</td>
                                            <td class="col-severity">
                                                <span class="severity-badge severity-{{ d.Severity | lowercase }}">
                                                    {{ d.Severity }}
                                                </span>
                                            </td>
                                            <td class="col-mf">{{ d.MemberFirmCode }}</td>
                                            <td class="col-description" [title]="d.Description">{{ d.Description }}</td>
                                            <td class="col-actual cell-clip" [title]="d.ActualValue">{{ d.ActualValue }}</td>
                                            <td class="col-threshold cell-clip" [title]="d.ThresholdValue">{{ d.ThresholdValue }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </ng-container>
                    </section>

                    <!-- Attribute Violations (per-employee null/blank and field-change details) -->
                    <ng-container *ngIf="getFieldChanges(panelState[r.QuarantineId]?.details) as fieldRows">
                        <section class="section-block" *ngIf="fieldRows.length > 0">
                            <h3>Attribute Violations</h3>
                            <div class="violations-table-wrap">
                                <table class="table table-sm dq-gate-table violations-table">
                                    <thead>
                                        <tr>
                                            <th class="col-rule">Field</th>
                                            <th class="col-mf">MF</th>
                                            <th class="col-person">Person ID</th>
                                            <th class="col-email">Email</th>
                                            <th class="col-actual">Old Value</th>
                                            <th class="col-new-value">New Value</th>
                                            <th class="col-threshold">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr *ngFor="let d of fieldRows">
                                            <td class="col-rule cell-clip" [title]="d.RuleKey">{{ d.RuleKey }}</td>
                                            <td class="col-mf">{{ d.MemberFirmCode }}</td>
                                            <td class="col-person">{{ d.PersonId || '—' }}</td>
                                            <td class="col-email cell-clip" [title]="d.Email || ''">{{ d.Email || '—' }}</td>
                                            <td class="col-actual cell-clip" [title]="d.OldValue || ''">{{ d.OldValue || '—' }}</td>
                                            <td class="col-new-value cell-clip" [title]="d.NewValue || ''">{{ d.NewValue || '—' }}</td>
                                            <td class="col-threshold cell-clip" [title]="d.Description || ''">{{ d.Description || '—' }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </ng-container>

                    <!-- Snapshot records (selectable) -->
                    <section class="section-block">
                        <h3>Snapshot records</h3>
                        <div *ngIf="(panelState[r.QuarantineId]?.snapshots?.length || 0) === 0" class="no-records">
                            No snapshot records.
                        </div>
                        <div *ngIf="(panelState[r.QuarantineId]?.snapshots?.length || 0) > 0"
                             class="snapshot-table-wrap">
                            <table class="table table-sm dq-gate-table snapshot-table">
                                <thead>
                                    <tr>
                                        <th class="col-check">
                                            <input type="checkbox"
                                                   [checked]="isAllSelected(r.QuarantineId)"
                                                   [indeterminate]="isPartialSelected(r.QuarantineId)"
                                                   (change)="toggleSelectAll(r.QuarantineId, $any($event.target).checked)"
                                                   [disabled]="!readWriteAccess"
                                                   title="Select all" />
                                        </th>
                                        <th class="col-name">Name</th>
                                        <th class="col-email">Email</th>
                                        <th class="col-person">Person ID</th>
                                        <th class="col-mf">MF</th>
                                        <th class="col-status">Status</th>
                                        <th class="col-job">Job Title</th>
                                        <th class="col-updated">Updated</th>
                                        <th class="col-reason">Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let s of panelState[r.QuarantineId].snapshots"
                                        [class.row-selected]="isSnapshotSelected(r.QuarantineId, s.SnapshotId)">
                                        <td class="col-check">
                                            <input type="checkbox"
                                                   [checked]="isSnapshotSelected(r.QuarantineId, s.SnapshotId)"
                                                   (change)="toggleSnapshot(r.QuarantineId, s.SnapshotId, $any($event.target).checked)"
                                                   [disabled]="!readWriteAccess" />
                                        </td>
                                        <td class="col-name cell-clip"
                                            [title]="s.PreferredFullName || (s.FirstName + ' ' + s.LastName)">
                                            {{ s.PreferredFullName || (s.FirstName + ' ' + s.LastName) }}
                                        </td>
                                        <td class="col-email cell-clip" [title]="s.Email || ''">{{ s.Email || '—' }}</td>
                                        <td class="col-person cell-clip" [title]="s.PersonId || ''">{{ s.PersonId || '—' }}</td>
                                        <td class="col-mf">{{ s.MemberFirmCode }}</td>
                                        <td class="col-status cell-clip"
                                            [title]="s.EmployeeStatusDesc || s.EmployeeStatusCode || ''">
                                            {{ s.EmployeeStatusDesc || s.EmployeeStatusCode || '—' }}
                                        </td>
                                        <td class="col-job cell-clip" [title]="s.JobTitle || ''">{{ s.JobTitle || '—' }}</td>
                                        <td class="col-updated">{{ s.UpdatedDate | date:'short' }}</td>
                                        <td class="col-reason cell-clip" [title]="s.Reason || ''">{{ s.Reason || '—' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                </ng-container>
            </div>
        </div>
    </div>

    <!-- Approve confirmation -->
    <div *ngIf="isApproveModalVisible" class="modal-backdrop" (click)="cancelAction()">
        <div class="confirm-modal confirm-modal--success" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <div class="modal-header-icon">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="17" height="17"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                </div>
                <h4 class="modal-title">Confirm Approve</h4>
                <button class="modal-close" (click)="cancelAction()" [disabled]="isSubmitting" aria-label="Close">&#215;</button>
            </div>
            <div class="modal-body">
                <p class="modal-description">
                    Approve <strong>{{ pendingActionSnapshotIds.length }}</strong> selected snapshot record(s) from quarantine
                    <span class="quarantine-id">{{ pendingActionQuarantineId }}</span>?
                </p>
                <p class="modal-notice">
                    Selected records will be marked Approved and leave the review queue.
                    A separate hourly process will apply them later. This action cannot be undone.
                </p>
            </div>
            <div class="modal-footer">
                <button class="modal-btn modal-btn--ghost" (click)="cancelAction()" [disabled]="isSubmitting">Cancel</button>
                <button class="modal-btn modal-btn--success" (click)="confirmApprove()" [disabled]="isSubmitting">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                    Approve
                </button>
            </div>
        </div>
    </div>

    <!-- Reject confirmation -->
    <div *ngIf="isConfirmModalVisible" class="modal-backdrop" (click)="cancelAction()">
        <div class="confirm-modal confirm-modal--danger" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <div class="modal-header-icon">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="17" height="17"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                </div>
                <h4 class="modal-title">Confirm Reject</h4>
                <button class="modal-close" (click)="cancelAction()" [disabled]="isSubmitting" aria-label="Close">&#215;</button>
            </div>
            <div class="modal-body">
                <p class="modal-description">
                    Discard <strong>{{ pendingActionSnapshotIds.length }}</strong> selected snapshot record(s) from quarantine
                    <span class="quarantine-id">{{ pendingActionQuarantineId }}</span>?
                </p>
                <p class="modal-notice">
                    Rejected records are removed from the snapshot and will not be restored to Stage.
                    If all records are discarded, this batch leaves the review queue.
                </p>
                <div class="form-group" style="margin-bottom:0">
                    <label for="rejectComment" class="field-label">
                        Reason / Comments
                        <span class="required-tag">required · min 10 characters</span>
                    </label>
                    <textarea #rejectTextarea
                              id="rejectComment"
                              name="rejectComment"
                              class="field-textarea"
                              rows="3"
                              maxlength="2000"
                              placeholder="Enter reason for rejection (minimum 10 characters)…"
                              [(ngModel)]="rejectComment"
                              (blur)="onRejectCommentBlur()"
                              [disabled]="isSubmitting"></textarea>
                    <div class="field-hint"
                         [class.field-hint--valid]="isRejectCommentValid"
                         [class.field-hint--error]="rejectCommentTouched && !isRejectCommentValid">
                        <ng-container *ngIf="!isRejectCommentValid">
                            {{ 10 - (rejectComment?.trim()?.length ?? 0) }} character(s) remaining
                        </ng-container>
                        <ng-container *ngIf="isRejectCommentValid">
                            ✓ Minimum length met
                        </ng-container>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                <button class="modal-btn modal-btn--ghost" (click)="cancelAction()" [disabled]="isSubmitting">Cancel</button>
                <button class="modal-btn modal-btn--danger" (click)="confirmReject()" [disabled]="isSubmitting || !isRejectCommentValid">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                    Reject
                </button>
            </div>
        </div>
    </div>

    <!-- Retry confirmation -->
    <div *ngIf="isRetryModalVisible" class="modal-backdrop" (click)="cancelAction()">
        <div class="confirm-modal confirm-modal--warning" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <div class="modal-header-icon">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="17" height="17"><path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/></svg>
                </div>
                <h4 class="modal-title">Confirm Retry</h4>
                <button class="modal-close" (click)="cancelAction()" [disabled]="isSubmitting" aria-label="Close">&#215;</button>
            </div>
            <div class="modal-body">
                <p class="modal-description">
                    Reset quarantine <span class="quarantine-id">{{ pendingRetryQuarantineId }}</span>
                    so it is reprocessed on the next sync run?
                </p>
                <p class="modal-notice">
                    The quarantine status will be set to RetryApproved. The next GlobalEmployee sync
                    will attempt to apply the snapshot records again.
                </p>
            </div>
            <div class="modal-footer">
                <button class="modal-btn modal-btn--ghost" (click)="cancelAction()" [disabled]="isSubmitting">Cancel</button>
                <button class="modal-btn modal-btn--warning" (click)="confirmRetry()" [disabled]="isSubmitting">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/></svg>
                    Retry
                </button>
            </div>
        </div>
    </div>

</div>
</div>

<opm-footer></opm-footer>
